import {
  CloudAccountType,
  GetAzureEonApplicationApi,
  GetSqlVmNetworkInfoApi,
  OnboardingAzureResourceGroup,
  type AzureConnectAccountTerraformRequest,
  type AzureConnectAccountTerraformRequestAccountTypeEnum,
  type AzureOnboardingRequest,
  type Configuration,
} from '@repo/api-gw-sdk';
import {
  AzureOnboardingRequestAccountTypeEnum,
  ConnectAzureAccountToEonTerraformApi,
  GetAzureAccountConnectIdApi,
  GetAzureConsentUrlApi,
  GetAzureOnboardingStatusApi,
  GetAzureSubscriptionsApi,
  OnboardAzureSubscriptionsApi,
  AzureSubscription,
  AzureManagementGroup,
  GetAzureAccountAppIdApi,
  GetAzureServicePrincipalIdsApi,
  ListAzureSubscriptionResourceGroupsApi,
  ValidateResourceConfigurationApi,
  BatchValidateResourceConfigurationApi,
} from '@repo/api-gw-sdk';

import type { HttpClient } from './httpClient';

const baseUrl = '/azure';

export const azure = (
  httpClient: HttpClient,
  config: Configuration,
  currentProjectId: string
) => {
  const consentUrlApi = new GetAzureConsentUrlApi(config);
  const connectAzureAccountTerraformApi =
    new ConnectAzureAccountToEonTerraformApi(config);
  const applicationIdApi = new GetAzureAccountAppIdApi(config);
  const servicePrincipalIdsApi = new GetAzureServicePrincipalIdsApi(config);
  const listSubscriptionsApi = new GetAzureSubscriptionsApi(config);
  const onboardSubscriptionsApi = new OnboardAzureSubscriptionsApi(config);
  const onboardingStatusApi = new GetAzureOnboardingStatusApi(config);
  const connectAzureAccountApi = new GetAzureAccountConnectIdApi(config);
  const listAzureSubscriptionResourceGroupsApi =
    new ListAzureSubscriptionResourceGroupsApi(config);
  const validateResourceConfigurationApi = new ValidateResourceConfigurationApi(
    config
  );
  const getAzureEonApplicationApi = new GetAzureEonApplicationApi(config);
  const batchValidateResourceConfigurationApi =
    new BatchValidateResourceConfigurationApi(config);
  const getSqlVmNetworkInfoApi = new GetSqlVmNetworkInfoApi(config);

  return {
    listSubscriptionResourceGroups: (
      subscriptionId: string,
      requestId: string
    ) =>
      httpClient.execute(
        [baseUrl, subscriptionId, requestId, currentProjectId],
        async () => {
          if (!subscriptionId) {
            return undefined;
          }

          return listAzureSubscriptionResourceGroupsApi.listAzureSubscriptionResourceGroups(
            currentProjectId,
            subscriptionId,
            requestId
          );
        }
      ),
    getConsentUrl: (requestId: string, tenantId?: string) =>
      consentUrlApi.getAzureConsentUrl(currentProjectId, requestId, tenantId),
    listSubscriptions: (requestId: string) =>
      listSubscriptionsApi.getAzureSubscriptions(currentProjectId, requestId),
    onboardSubscriptions: (
      requestId: string,
      accountType: CloudAccountType,
      tenantId: string,
      selectedEntity:
        | AzureManagementGroup
        | AzureSubscription
        | OnboardingAzureResourceGroup
    ) => {
      const onboardingAccountType: AzureOnboardingRequestAccountTypeEnum =
        GetAccountType(accountType);

      const subscriptions: AzureSubscription[] = [];
      const managementGroups: AzureManagementGroup[] = [];
      const resourceGroups: OnboardingAzureResourceGroup[] = [];
      if (selectedEntity instanceof AzureSubscription) {
        subscriptions.push(selectedEntity);
      }

      if (selectedEntity instanceof AzureManagementGroup) {
        managementGroups.push(selectedEntity);
      }

      if (selectedEntity instanceof OnboardingAzureResourceGroup) {
        resourceGroups.push(selectedEntity);
      }

      return onboardSubscriptionsApi.onboardAzureSubscriptions(
        currentProjectId,
        requestId,
        {
          accountType: onboardingAccountType,
          tenantId: tenantId,
          subscriptions,
          managementGroups,
          resourceGroups,
        } as AzureOnboardingRequest
      );
    },
    connectAccountTerraform: (
      accountType: CloudAccountType,
      tenantId: string,
      subscriptionId?: string,
      managementGroupId?: string,
      restoreAppId?: string,
      restoreLocation?: string
    ) => {
      const onboardingAccountType = GetAccountType(
        accountType
      ) as unknown as AzureConnectAccountTerraformRequestAccountTypeEnum;
      return connectAzureAccountTerraformApi.connectAzureAccountToEonTerraform(
        currentProjectId,
        {
          accountType: onboardingAccountType,
          tenantId,
          subscriptionId,
          managementGroupId,
          restoreAppId,
          restoreIdentityLocation: restoreLocation,
        } as AzureConnectAccountTerraformRequest
      );
    },
    getOnboardingStatus: (requestId: string) =>
      onboardingStatusApi.getAzureOnboardingStatus(currentProjectId, requestId),
    getApplicationId: () => {
      return httpClient.execute(
        [baseUrl, 'getApplicationId', currentProjectId],
        () => applicationIdApi.getAzureAccountAppId(currentProjectId)
      );
    },
    getServicePrincipalId: () => {
      return httpClient.execute(
        [baseUrl, 'getServicePrincipalId', currentProjectId],
        () =>
          servicePrincipalIdsApi.getAzureServicePrincipalIds(currentProjectId)
      );
    },
    getAccountConnectId: () => {
      return httpClient.execute(
        [baseUrl, 'getAccountConnectId', currentProjectId],
        () => connectAzureAccountApi.getAzureAccountConnectId(currentProjectId)
      );
    },
    validateResourceConfiguration: (resourceId: string) => {
      return validateResourceConfigurationApi.validateResourceConfiguration(
        currentProjectId,
        {
          resourceId,
        }
      );
    },
    batchValidateResourceConfiguration: (resourceIds: string[]) => {
      return batchValidateResourceConfigurationApi.batchValidateResourceConfiguration(
        currentProjectId,
        {
          resourceIds,
        }
      );
    },
    getAzureEonApplication: () => {
      return httpClient.execute(
        [baseUrl, 'getAzureEonApplication', currentProjectId],
        () => getAzureEonApplicationApi.getAzureEonApplication(currentProjectId)
      );
    },
    getSqlVmNetworkInfo: (params: {
      cloudAccountId: string;
      resourceGroup: string;
      vmName: string;
    }) => {
      const enabled = Boolean(
        params.cloudAccountId && params.resourceGroup && params.vmName
      );
      return httpClient.execute(
        enabled
          ? [
              baseUrl,
              'getSqlVmNetworkInfo',
              currentProjectId,
              params.cloudAccountId,
              params.resourceGroup,
              params.vmName,
            ]
          : null,
        () =>
          getSqlVmNetworkInfoApi.getSqlVmNetworkInfo(currentProjectId, {
            cloudAccountId: params.cloudAccountId,
            resourceGroup: params.resourceGroup,
            vmName: params.vmName,
          })
      );
    },
    fetchSqlVmNetworkInfo: (params: {
      cloudAccountId: string;
      resourceGroup: string;
      vmName: string;
    }) =>
      getSqlVmNetworkInfoApi.getSqlVmNetworkInfo(currentProjectId, {
        cloudAccountId: params.cloudAccountId,
        resourceGroup: params.resourceGroup,
        vmName: params.vmName,
      }),
  };
};

function GetAccountType(accountType: CloudAccountType) {
  let onboardingAccountType: AzureOnboardingRequestAccountTypeEnum;
  if (accountType === CloudAccountType.Source) {
    onboardingAccountType = AzureOnboardingRequestAccountTypeEnum.Source;
  } else if (accountType === CloudAccountType.Restore) {
    onboardingAccountType = AzureOnboardingRequestAccountTypeEnum.Restore;
  } else if (accountType === CloudAccountType.Vault) {
    onboardingAccountType = AzureOnboardingRequestAccountTypeEnum.Vault;
  } else if (accountType === CloudAccountType.Scanning) {
    onboardingAccountType = AzureOnboardingRequestAccountTypeEnum.Scanning;
  } else {
    throw new Error(`Invalid account type: ${accountType}`);
  }
  return onboardingAccountType;
}
